import { Command } from "cmdk";
import { useEffect, useState } from "react";
import { Icon, type IconName } from "./Icon";

export interface PaletteAction {
  id: string;
  label: string;
  group: string;
  icon?: IconName;
  hint?: string;
  run: () => void;
}

/**
 * ⌘K.
 *
 * cmdk rather than hand-written: a palette is a combobox with a filtered listbox,
 * `aria-activedescendant`, virtual focus and typeahead. Getting that right by hand is a
 * week and a source of subtle screen-reader bugs, and it is the one piece of this console
 * with no product identity in it — every palette works the same way on purpose.
 *
 * Everything the product IS — shell, plate, panes, composer, window meter — stays
 * hand-written, because none of it exists in any kit.
 */
export function CommandPalette({ actions }: { actions: PaletteAction[] }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const groups = [...new Set(actions.map((a) => a.group))];

  return (
    <Command.Dialog
      open={open}
      onOpenChange={setOpen}
      label="Commands"
      className="palette"
      overlayClassName="palette-scrim"
      contentClassName="palette-panel"
    >
      <div className="palette-input">
        <Icon name="search" />
        <Command.Input placeholder="Search commands…" />
      </div>
      <Command.List>
        <Command.Empty className="palette-empty">
          Nothing matches. Try a shorter word.
        </Command.Empty>
        {groups.map((g) => (
          <Command.Group key={g} heading={g}>
            {actions
              .filter((a) => a.group === g)
              .map((a) => (
                <Command.Item
                  key={a.id}
                  value={`${a.group} ${a.label}`}
                  onSelect={() => {
                    setOpen(false);
                    a.run();
                  }}
                >
                  {a.icon && <Icon name={a.icon} size={14} />}
                  <span className="palette-label">{a.label}</span>
                  {a.hint && <kbd className="palette-hint">{a.hint}</kbd>}
                </Command.Item>
              ))}
          </Command.Group>
        ))}
      </Command.List>
    </Command.Dialog>
  );
}
